import { useRef, useState } from "react";
import { EyeOff } from "lucide-react";
import { Action, Panel, PanelHeader } from "./ui";
import { useScrollIntoViewWhen } from "@/lib/scrollTo";

const TABS = [
  { key: "counselor", label: "Conselheiros" },
  { key: "psychologist", label: "Psicólogos(as)" },
  { key: "psychiatrist", label: "Psiquiatras" },
];

/** Visão de alocação da equipe: carga atual, altas próximas e carga projetada. */
export const AllocationPanel = ({ data, onClose, backLabel = "Ocultar" }) => {
  const [tab, setTab] = useState("counselor");
  const sectionRef = useRef(null);
  useScrollIntoViewWhen(sectionRef, true);
  const role = data?.roles?.[tab];

  return (
    <div ref={sectionRef} className="scroll-mt-32">
      <Panel testId="meeting-allocation-panel" className="space-y-5">
      <PanelHeader
        label="Alocação da equipe"
        title="Carga por profissional"
        hint={`Altas previstas nos próximos ${data?.horizon_days ?? 10} dias, a partir da data prevista de alta cadastrada no paciente.${
          data?.ward && data.ward !== "all"
            ? ` Contagens restritas à ala ${data.ward_label} (${data.filtered_patients ?? 0} paciente(s)).`
            : ""
        }`}
        actions={
          <Action variant="secondary" size="sm" icon={EyeOff} data-testid="meeting-allocation-close" onClick={onClose}>
            {backLabel}
          </Action>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <span
          data-testid="allocation-ward-indicator"
          className="inline-flex items-center rounded-full bg-sage-50 px-3 py-1 text-[13px] font-semibold text-sage-700"
        >
          Ala: {data?.ward_label || "Todas as alas"}
        </span>
        {data?.ward && data.ward !== "all" ? (
          <span className="text-[13px] text-ink-500">Totais calculados apenas com os pacientes desta ala.</span>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            data-testid={`allocation-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`min-h-[38px] rounded-full px-4 text-[13.5px] font-semibold transition-colors ${
              tab === t.key ? "bg-sage-600 text-white" : "border border-border bg-white text-ink-700 hover:bg-sand"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl border border-border">
        <table data-testid={`allocation-table-${tab}`} className="w-full text-left">
          <thead>
            <tr className="border-b border-border bg-sand/45">
              {["Profissional", "Pacientes ativos", "Altas em 10 dias", "Carga projetada"].map((h) => (
                <th key={h} className="px-4 py-3 text-[12px] font-semibold uppercase tracking-[0.1em] text-ink-500">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border/70">
            {(role?.rows || []).map((r) => (
              <tr key={r.staff_id} data-testid={`allocation-row-${r.staff_id}`} className="hover:bg-sand/35">
                <td className="px-4 py-3 text-[14.5px] text-ink-800">{r.name}</td>
                <td className="px-4 py-3 text-[14.5px] text-ink-700">{r.active_patients}</td>
                <td className="px-4 py-3 text-[14.5px] text-ink-700">{r.discharging_soon}</td>
                <td className="px-4 py-3 text-[14.5px] font-semibold text-ink-800">{r.projected_load}</td>
              </tr>
            ))}
            {(role?.rows || []).length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-4 text-[14px] text-ink-500">
                  Nenhum funcionário ativo com este cargo.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <p data-testid={`allocation-unassigned-${tab}`} className="text-[13.5px] text-ink-600">
        Pacientes sem {role?.label?.toLowerCase() || "profissional"} definido: {role?.unassigned_patients ?? 0}
      </p>
      </Panel>
    </div>
  );
};
